import React from 'react';
import { Check } from 'lucide-react';
import { usePasswordPolicy, evaluatePassword, PasswordPolicy } from '../../hooks/usePasswordPolicy';

/**
 * Live password requirement checklist, driven by the server-side policy.
 *
 * Renders nothing until the user types. Shows only the rules the client can
 * actually evaluate (length, and composition if ever re-enabled), plus a
 * short guidance line covering the rules that are enforced server-side —
 * breach screening and name/email screening.
 *
 * IMPORTANT: this component never asserts a password is acceptable. Passing
 * every visible check does not guarantee the server will accept it. Callers
 * must surface the server's error on submit.
 */
interface PasswordRequirementsProps {
  password: string;
  /** Pass a policy to avoid a duplicate fetch when the parent already has one. */
  policy?: PasswordPolicy;
  /** Two columns suits wide forms; one suits narrow modals. */
  columns?: 1 | 2;
  style?: React.CSSProperties;
  /** Show the "longer is stronger" guidance line. Default true. */
  showGuidance?: boolean;
}

const uncheckedCircle = (
  <span
    style={{
      width: 14,
      height: 14,
      borderRadius: '50%',
      border: '2px solid var(--v2-border)',
      display: 'inline-block',
      flexShrink: 0,
    }}
  />
);

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  policy: policyProp,
  columns = 2,
  style,
  showGuidance = true,
}) => {
  const { policy: fetchedPolicy } = usePasswordPolicy();
  const policy = policyProp || fetchedPolicy;

  if (!password) return null;

  const { checks } = evaluatePassword(password, policy);

  return (
    <div
      style={{
        marginTop: '10px',
        padding: '10px 12px',
        backgroundColor: 'var(--v2-surface-alt)',
        borderRadius: 'var(--radius-sm)',
        ...style,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: columns === 2 ? '1fr 1fr' : '1fr',
          gap: '4px 16px',
        }}
      >
        {checks.map((item) => (
          <div
            key={item.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.8125rem',
              color: item.ok ? 'var(--v2-success)' : 'var(--v2-text-muted)',
            }}
          >
            {item.ok ? <Check size={14} strokeWidth={2.5} /> : uncheckedCircle}
            {item.label}
          </div>
        ))}
      </div>

      {showGuidance && (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            color: 'var(--v2-text-tertiary)',
          }}
        >
          {policy.screensBreachedPasswords
            ? 'Tip: a few random words make a strong password. Passwords found in known data breaches, or containing your name or email, will be rejected.'
            : 'Tip: a few random words make a strong password.'}
        </p>
      )}
    </div>
  );
};

export default PasswordRequirements;
